import React, { useRef, useState, useEffect } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useSharedTranslations } from "../../lib/i18n";
import { DynamicInput } from "../dynamic-input/dinamic-input";
import { Button } from "../button/button";
import { Icon } from "../icons/icon";
import { APP_EMITTER_EVENTS, AppEmitter, IValidateError } from "../../lib";
import { SortableList } from "../sortable-list/sortable-list";
import styles from "./input-list.module.scss";

interface IInputListProps {
    listId: string | number;
    list?: string[];
    list_en?: string[];
    validate?: boolean;
    validateFunc?: (value: string[]) => IValidateError;
    triggerValidate?: number;
    enLanguageEnabled?: boolean;
    className?: string;
    onChange: (val: InputListItemType[]) => void;
    name?: string;
}

export interface InputListItemType {
    value: string;
    value_en: string;
    id: string | number;
}


export const InputList: React.FC<IInputListProps> = React.memo(({
                                                                    listId,
                                                                    list,
                                                                    list_en,
                                                                    validate,
                                                                    triggerValidate,
                                                                    validateFunc,
                                                                    enLanguageEnabled,
                                                                    className,
                                                                    onChange,
                                                                    name = "input_list"
                                                                }) => {
    const idRef = useRef(0);
    const [sortableComponents, setSortableComponents] = useState<InputListItemType[]>([]);
    const t = useSharedTranslations();
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const { i18n } = useTranslation();

    useEffect(() => {
        if (triggerValidate && validate) {
            validateValue(sortableComponents);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [triggerValidate, validate, validateFunc, sortableComponents]);

    useEffect(() => {
        if (isError) {
            validateValue(sortableComponents);
        }
    }, [i18n.language]);

    // listId используется для различения компонентов InputList на одной форме,
    // сейчас в качестве listId передаются component.id, которые назначаются полям при загрузке формы.
    // onChange или list не получилось так использовать, ошибка "Maximum update depth exceeded"
    useEffect(() => {
        if (!Array.isArray(list)) return;
        setSortableComponents(
            list.map((value, index) => ({
                value,
                value_en: Array.isArray(list_en) ? list_en[index] : "",
                id: idRef.current++
            }))
        );
    }, [listId]);

    const validateValue = (val: InputListItemType[]) => {
        const value = val.map(c => c.value).filter(c => !!c);
        let error = false;
        let message: string | undefined = undefined;
        if (validateFunc) {
            const validation = validateFunc(value);
            error = !validation.valid;
            message = validation.message;
        }
        setIsError(error);
        setValidationError(message);
    };

    const setValue = (name: keyof InputListItemType, val: string, index: number) => {

        setSortableComponents((prevState) => {
            prevState[index][name] = val;
            onChange(prevState);
            validateValue(prevState);
            return prevState;
        });
    };

    const onDeleteAllClick = () => {
        onChange([]);
        setSortableComponents([]);
    };

    const onDeleteOption = (index: number) => {
        setSortableComponents((prevState) => {
            prevState.splice(index, 1);
            onChange(prevState);
            return prevState;
        });
    };

    const onComponentsSort = (options: InputListItemType[]) => {
        onChange(options);
        setSortableComponents(options);
    };

    const onAddOptionClick = () => {
        setSortableComponents((prevState) => {
            const id = idRef.current++;
            prevState.push({
                value: "",
                value_en: "",
                id: id
            });
            onChange(prevState);
            setTimeout(() => {
                const lastInput = document.getElementById(`select-container-input-${name}-${id}`);
                if (lastInput) {
                    AppEmitter.emit(APP_EMITTER_EVENTS.blockLayoutScroll);
                    setTimeout(() => {
                        lastInput.scrollIntoView({ behavior: "smooth", block: "center" });
                        lastInput.focus();
                    }, 100);
                }
            });
            return prevState;
        });

    };


    return <div className={cn(styles.container, className)}>

        {!Array.isArray(list) || list.length === 0 ||
            <div className={styles.actionContainer}>
                <div className={styles.listActionButtons}>
                    <div className={styles.buttonGroup}>
                        <Button size="m"
                                onClick={onDeleteAllClick}
                                view="outline">
                            <div className={styles.optionText}>
                                <Icon glyph="trash" className={styles.trashIcon}/>
                                <span>{t("deleteAll")}</span>
                            </div>
                        </Button>
                    </div>
                </div>
            </div>}

        <div className={styles.sortableContainer}>
            <SortableList<InputListItemType>
                items={sortableComponents}
                onChange={onComponentsSort}
                renderItem={(item: InputListItemType, index: number) => (
                    <SortableList.Item id={item.id}>
                        <SortableList.DragHandle/>
                        <div className={styles.selectContainer} id={`select-container-${name}-${item.id}`}>
                            <div className={styles.inputField}>
                                <DynamicInput size="m"
                                              id={`select-container-input-${name}-${item.id}`}
                                              value={item.value}
                                              textareaEdge={enLanguageEnabled ? 25 : 60}
                                              onChange={(e: any) => {
                                                  setValue("value", e.target.value as string, index);
                                              }}
                                              view="outline"
                                              name={"li-" + index}
                                />
                            </div>

                            {enLanguageEnabled &&
                                <div className={styles.inputField}>
                                    <DynamicInput size="m"
                                                  id={`select-container-input-en-${name}-${item.id}`}
                                                  value={item.value_en}
                                                  textareaEdge={25}
                                                  onChange={(e: any) => {
                                                      setValue("value_en", e.target.value as string, index);
                                                  }}
                                                  view="outline"
                                                  name={"li-en" + index}
                                                  placeholder="[En]"
                                    />
                                </div>
                            }


                            <div onClick={() => onDeleteOption(index)}><Icon glyph="trash"
                                                                             className={styles.deleteIcon}/>
                            </div>
                        </div>
                    </SortableList.Item>
                )}
            />
        </div>

        <Button size="m"
                className={styles.addOptionButton}
                onClick={onAddOptionClick}
                view="outline">
            <div className={styles.optionText}>
                <Icon glyph="plusCircle"/>
                <span>{t("addValue")}</span>
            </div>
        </Button>
        {isError && <div className="Textinput_state_error">
            <div className={cn(styles.validationError, "Textinput-Hint")}>
                {validationError}
            </div>
        </div>}
    </div>;
});
